/**
 * Authentication store using Zustand
 * Manages JWT token, user state, and redirect URL for post-login navigation
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState } from '@/types/api';

const AUTH_STORAGE_KEY = 'metadata-ui-auth';

interface AuthStore extends AuthState {
  // Actions
  login: (token: string, username: string) => void;
  logout: () => void;
  setRedirectUrl: (url: string | null) => void;
  clearRedirectUrl: () => string | null;
  getToken: () => string | null;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      username: null,
      isAuthenticated: false,
      redirectUrl: null,

      // Actions
      login: (token: string, username: string) => {
        set({
          token,
          username,
          isAuthenticated: true,
        });
      },

      logout: () => {
        set({
          token: null,
          username: null,
          isAuthenticated: false,
          redirectUrl: null,
        });
      },

      setRedirectUrl: (url: string | null) => {
        set({ redirectUrl: url });
      },

      clearRedirectUrl: () => {
        const currentUrl = get().redirectUrl;
        set({ redirectUrl: null });
        return currentUrl;
      },

      getToken: () => {
        return get().token;
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      partialize: (state) => ({
        token: state.token,
        username: state.username,
        isAuthenticated: state.isAuthenticated,
        // Don't persist redirectUrl - it's only needed for the current session
      }),
    }
  )
);

/**
 * Get the current token (non-reactive, for use in API calls)
 */
export function getAuthToken(): string | null {
  return useAuthStore.getState().token;
}

/**
 * Check if user is authenticated (non-reactive)
 */
export function isAuthenticated(): boolean {
  return useAuthStore.getState().isAuthenticated;
}

/**
 * Logout and set redirect URL (for 401/403 handling)
 */
export function handleAuthError(redirectUrl: string): void {
  const store = useAuthStore.getState();
  store.setRedirectUrl(redirectUrl);
  store.logout();
}
